import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { Folder, Upload as UploadIcon } from "react-feather";
import {
  Box,
  Chip as MuiChip,
  Typography,
  Button as MuiButton,
  List,
  Grid as MuiGrid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import IconButton from "@material-ui/core/IconButton";
import {
  ArrowDropDown,
  ArrowRight,
  Delete as MuiDelete,
} from "@material-ui/icons";
import { rgba } from "polished";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useApp } from "../../AppProvider";
import Loader from "../Loader";
import { DEFAULT_ATTACHED_DOCUMENTS_STATE } from "../../pages/dataManagement/wells/PermitsManagement/PermitsConstants";

const Grid = styled(MuiGrid)(spacing);
const Button = styled(MuiButton)(spacing);
const Chip = styled(MuiChip)(spacing);

const Delete = styled(MuiDelete)`
  color: #bc1a3d;
`;

const FieldItem = styled.div`
  border-left: 3px solid ${(props) => props.theme.palette.background.toolbar};
  padding-left: 12px;
  &.error {
    border-color: red;
  }
`;

const FieldToggleIcon = styled(IconButton)`
  // position: absolute;
  // left: -22px;
  margin-right: 5px;
  width: 18px;
  height: 18px;
  border-radius: 2px;

  &,
  &:active,
  &:focus,
  &:focus-within {
    background-color: ${(props) => props.theme.palette.background.toolbar};
  }

  svg {
    // position: absolute;
    width: 22px;
    height: 22px;
  }

  @media (hover: none) {
    &:hover {
      background-color: ${(props) => props.theme.palette.background.toolbar};
    }
  }
`;

const FieldIconLess = styled(ArrowRight)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const FieldIconMore = styled(ArrowDropDown)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const Label = styled(Typography)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  margin-bottom: ${(props) => props.theme.spacing(3)}px;
`;

const UploadPermits = ({
  selectedNdx,
  data,
  isLoading,
  error,
  refetch,
  lookups,
  state,
  setter,
  handleUpdateState,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();

  const [selectedFiles, setSelectedFiles] = useState([]);
  //attachments accordion
  const [isOpen, setIsOpen] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    setSelectedFiles([]);
    setConflictAlert(false);
    setFileToDelete({});
  }, [selectedNdx]);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(files);
  };

  const endpoint = "assoc-permits-to-attachments";

  const [conflictAlert, setConflictAlert] = useState([]);
  useEffect(() => {
    if (selectedFiles.length > 0) {
      const conflicts = selectedFiles
        .map(
          (file) =>
            data?.find((attachment) => attachment.system_filename === file.name)
              ?.system_filename
        )
        .filter(Boolean);
      setConflictAlert(conflicts);
    } else {
      setConflictAlert([]);
    }
  }, [selectedFiles, data]);

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const [fileToDelete, setFileToDelete] = useState({});
  const handleDeleteModal = (e, file) => {
    e.preventDefault();
    setDeleteDialogOpen(true);
    setFileToDelete(file);
  };

  //uploads selected files to s3 bucket
  const [isUploading, setIsUploading] = useState(false);
  const uploadFiles = async () => {
    if (!selectedFiles.length) {
      doToast("warning", "No files selected");
      return null;
    }

    setIsUploading(true);
    let successCount = 0;
    let failedFiles = [];

    try {
      const token = await getAccessTokenSilently();
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Upload files sequentially to avoid overwhelming the server
      for (const file of selectedFiles) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("permit_ndx", selectedNdx);
          formData.append("tags", JSON.stringify([state.tags]));
          formData.append("notes", state.notes || "");

          await axios.post(
            `${process.env.REACT_APP_ENDPOINT}/api/${endpoint}`,
            formData,
            { headers }
          );
          successCount++;
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || error.message || "Unknown error";
          failedFiles.push({ name: file.name, error: errorMessage });
        }
      }

      // Show appropriate toast messages
      if (successCount > 0) {
        doToast(
          "success",
          `${successCount} file${
            successCount > 1 ? "s" : ""
          } uploaded successfully`
        );
      }
      if (failedFiles.length > 0) {
        const failedNames = failedFiles.map((f) => f.name).join(", ");
        doToast("error", `Failed to upload: ${failedNames}`);
      }

      await refetch();
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "Upload failed";
      doToast("error", message);
    } finally {
      setIsUploading(false);
      setSelectedFiles([]);
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    handleCloseDialog();

    try {
      if (fileToDelete) {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        await axios.patch(
          `${process.env.REACT_APP_ENDPOINT}/api/${endpoint}/${fileToDelete.att_ndx}`,
          {},
          { headers }
        );
        //refetches data to include newly added attachment
        await refetch();
        doToast("success", "Record was deleted successfully");
      } else {
        doToast("error", "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      const message = err?.message ?? "Something went wrong";
      doToast("error", message);
    }
  };

  const ConfirmDeleteDialog = () => {
    return (
      <Dialog open={deleteDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle style={{ textAlign: "center" }}>
          {"Delete Record"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ textAlign: "center" }}>
            Are you sure you want to delete{" "}
            <strong>{fileToDelete.original_filename}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={handleDelete}
            className="error"
            autoFocus
          >
            Yes, delete record
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <ConfirmDeleteDialog />

      <Box style={{ width: "100%" }}>
        {isLoading ? (
          <Loader />
        ) : (
          <FieldItem className={error ? "error" : ""}>
            <Grid container justify={"space-between"}>
              <Grid item>
                <FieldToggleIcon
                  size={"small"}
                  onClick={() => {
                    setIsOpen(!isOpen);
                  }}
                >
                  {isOpen ? <FieldIconMore /> : <FieldIconLess />}
                </FieldToggleIcon>
                <Label
                  color={error ? "error" : "textPrimary"}
                  style={{ display: "inline-block" }}
                >
                  Existing Attachments
                </Label>
              </Grid>
            </Grid>

            {isOpen &&
              (data?.length ? (
                <List style={{ padding: 0 }}>
                  {data.map((item) => {
                    return (
                      <div key={item.att_ndx}>
                        <Chip
                          mb={2}
                          label={item.original_filename}
                          color="primary"
                          icon={
                            <Folder style={{ "&&": { color: "orange" } }} />
                          }
                          clickable={true}
                          component="a"
                          href={item.system_filepath}
                          target="_blank"
                          onDelete={(e) => handleDeleteModal(e, item)}
                          variant="outlined"
                          deleteIcon={<Delete />}
                        />
                      </div>
                    );
                  })}
                </List>
              ) : (
                `No files are currently attached to this permit`
              ))}
          </FieldItem>
        )}

        {/* Divider between existing and new uploads */}
        <Box style={{ margin: "24px 0", borderTop: "2px solid #e0e0e0" }} />

        <Typography
          variant="h6"
          gutterBottom
          style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "16px" }}
        >
          Upload New Files
        </Typography>

        {/* Step 1: File Selection */}
        <Box style={{ marginBottom: "24px" }}>
          <Typography
            variant="h6"
            gutterBottom
            style={{ fontSize: "1rem", fontWeight: 500 }}
          >
            Step 1: Select Files
          </Typography>

          {/* Drag and Drop Zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => selectedNdx && fileInputRef.current?.click()}
            style={{
              border: isDragging ? "2px dashed #1976d2" : "2px dashed #eeeeee",
              borderRadius: "4px",
              padding: "32px 20px",
              textAlign: "center",
              cursor: selectedNdx ? "pointer" : "default",
              transition: "all 0.3s",
              backgroundColor: isDragging
                ? "rgba(33, 150, 243, 0.1)"
                : "transparent",
              marginBottom: "12px",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileInput}
              onClick={(event) => {
                event.stopPropagation();
                event.target.value = null;
                setter(DEFAULT_ATTACHED_DOCUMENTS_STATE);
              }}
            />
            <CloudUploadIcon
              style={{
                fontSize: "48px",
                color: isDragging ? "#2196f3" : "#bdbdbd",
                marginBottom: "8px",
              }}
            />
            <Typography variant="body1" gutterBottom>
              {isDragging
                ? "Drop the files here"
                : "Drag and drop files here, or click to select files"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Multiple files can be selected at once
            </Typography>
          </Box>

          {/* Browse Button */}
          <Box textAlign="center">
            <Button
              component="label"
              startIcon={<CloudUploadIcon />}
              variant="contained"
              color="primary"
              fullWidth
              disabled={!selectedNdx}
            >
              Browse Files
              <input
                type="file"
                multiple
                hidden
                onChange={handleFileInput}
                onClick={(event) => {
                  event.target.value = null;
                  setter(DEFAULT_ATTACHED_DOCUMENTS_STATE);
                }}
              />
            </Button>
          </Box>
        </Box>

        {/* Selected Files Display */}
        {selectedFiles.length > 0 && (
          <Box style={{ marginBottom: "24px" }}>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontSize: "1rem", fontWeight: 500 }}
              >
                Selected Files ({selectedFiles.length})
              </Typography>
              {selectedFiles.length > 1 && (
                <Button
                  size="small"
                  onClick={() => setSelectedFiles([])}
                  style={{ textTransform: "none" }}
                >
                  Remove All
                </Button>
              )}
            </Box>
            <Box
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "12px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {selectedFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  clickable={false}
                  color="primary"
                  icon={<Folder />}
                  onDelete={() => handleRemoveFile(index)}
                  variant="outlined"
                  size="small"
                  style={{ margin: "4px" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Step 2: File Metadata */}
        {selectedFiles.length > 0 && (
          <Box style={{ marginBottom: "24px" }}>
            <Typography
              variant="h6"
              gutterBottom
              style={{ fontSize: "1rem", fontWeight: 500 }}
            >
              Step 2: Set File Attributes
            </Typography>

            {conflictAlert.length > 0 && (
              <Alert severity="warning" style={{ marginBottom: "16px" }}>
                <AlertTitle>Warning</AlertTitle>
                <strong>
                  The following file{conflictAlert.length > 1 ? "s" : ""}{" "}
                  already exist{conflictAlert.length > 1 ? "" : "s"}:{" "}
                  {conflictAlert.join(", ")}
                </strong>
                <br />
                Proceeding will overwrite the existing file
                {conflictAlert.length > 1 ? "s" : ""}.
              </Alert>
            )}

            <Box
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "16px",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography
                variant="caption"
                color="textSecondary"
                gutterBottom
                display="block"
                style={{ marginBottom: "16px" }}
              >
                These attributes will be applied to all selected files
              </Typography>

              <Grid container spacing={2}>
                {/* Document Tag - Required */}
                <Grid item xs={12} sm={6}>
                  {lookups.attachmentTags.length > 0 && (
                    <FormControl
                      variant="outlined"
                      fullWidth
                      required
                      size="small"
                    >
                      <InputLabel id="document-tag">Document Tag *</InputLabel>
                      <Select
                        error={!state.tags}
                        labelId="document-tag-label"
                        id="document-tag"
                        label="Document Tag *"
                        value={state.tags || ""}
                        onChange={(e) =>
                          handleUpdateState("tags", e.target.value)
                        }
                      >
                        {lookups.attachmentTags.map((tag) => (
                          <MenuItem
                            key={tag.att_tag_ndx}
                            value={tag.att_tag_ndx}
                          >
                            {tag.att_tag_text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                {/* Notes field */}
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes (Optional)"
                    onChange={(e) => handleUpdateState("notes", e.target.value)}
                    value={state.notes ?? ""}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Step 3: Upload */}
        {selectedFiles.length > 0 && (
          <Box style={{ marginBottom: "24px" }}>
            <Typography
              variant="h6"
              gutterBottom
              style={{ fontSize: "1rem", fontWeight: 500 }}
            >
              Step 3: Upload Files
            </Typography>

            {isUploading ? (
              <Box style={{ textAlign: "center", padding: "20px" }}>
                <Loader />
                <Typography variant="body2" style={{ marginTop: "8px" }}>
                  Uploading files...
                </Typography>
              </Box>
            ) : (
              <Button
                startIcon={<UploadIcon />}
                variant="contained"
                color="secondary"
                fullWidth
                size="large"
                onClick={uploadFiles}
                disabled={!state.tags || !selectedNdx}
              >
                Upload {selectedFiles.length} File
                {selectedFiles.length > 1 ? "s" : ""}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default UploadPermits;
